import React from 'react';
import { useField } from 'formik';
import {
  Box,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { CardProps, GridProps } from '@mui/material';
import { DropdownOption } from 'types/common/DropdownOption';
import { useSchemaContext } from './SparkForm';
import { SparkImageLoader } from 'sharedComponents/UI';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';

type DisabledEntity = { value: string; helperComponent?: React.ReactNode };

type UiButtonProps = {
  isSelected: boolean;
  handleItemClick: (id: string) => void;
  option: DropdownOption;
  isDisabled?: boolean;
};

type SparkEnumButtonProps = {
  name: string;
  label: string;
  size?: GridProps['xs'];
  gridProps?: GridProps;
  helpText?: string;
  onChange?: (value: string[]) => void;
  subHeaderText?: string;
  disabledEntities?: DisabledEntity[];
  options: DropdownOption[];
};

const SparkEnumButton = ({
  name,
  label,
  size = 12,
  helpText,
  onChange,
  subHeaderText = '',
  gridProps,
  disabledEntities,
  options,
}: SparkEnumButtonProps) => {
  const [field, meta, helper] = useField<string[]>(name);
  const { validationSchema } = useSchemaContext();

  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      //@ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'required'
      );
      if (isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);

  const getDisabledState = (value: string) => {
    return disabledEntities?.find((entity) => entity.value === value);
  };

  const handleChange = (value: string) => {
    const currentValues = field.value || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    helper.setValue(newValues);
    if (onChange) {
      onChange(newValues);
    }
  };

  const isError = meta.touched && !!meta.error;

  return (
    <Grid item xs={size} direction='column' {...gridProps}>
      <InputLabel
        sx={{ marginLeft: 1, display: 'flex', gap: 0.5, fontSize: 16 }}
      >
        <span>{isFieldRequired && '*'}</span>
        {label}
        {helpText && (
          <Tooltip title={helpText} placement='top' arrow enterTouchDelay={0}>
            <InfoOutlinedIcon fontSize='small' />
          </Tooltip>
        )}
      </InputLabel>
      {subHeaderText && (
        <Typography
          color='#A3A3A3'
          marginLeft={1.6}
          fontSize={12}
          textAlign='left'
        >
          {subHeaderText}
        </Typography>
      )}
      <FormGroup>
        <Grid container xs={12} gap={2} justifyContent='center'>
          {options.map((option) => (
            <UiButton
              key={option.value}
              isSelected={field.value?.includes(`${option.value}`)}
              handleItemClick={handleChange}
              option={option}
              isDisabled={!!getDisabledState(`${option.value}`)}
            />
          ))}
        </Grid>
      </FormGroup>
      <FormHelperText
        sx={{
          color: (theme) => theme.palette.error.main,
          marginLeft: 1,
          marginBottom: 0.5,
        }}
      >
        {isError ? meta.error : ' '}
      </FormHelperText>
    </Grid>
  );
};
export default SparkEnumButton;

function UiButton({
  isSelected,
  handleItemClick,
  option,
  isDisabled = false,
}: UiButtonProps) {
  const selectedStyle: CardProps['sx'] = {
    bgcolor: (theme) => theme.palette.primary.main,
    color: (theme) => theme.palette.primary.light,
  };
  const unSelectedStyle: CardProps['sx'] = {
    bgcolor: 'white',
    color: (theme) => theme.palette.primary.main,
    borderColor: (theme) => theme.palette.borderColor,
  };
  // const selectedStyle: CardProps['sx'] = {
  //   bgcolor: (theme) => theme.palette.primary.light,
  //   color: (theme) => theme.palette.primary.main,
  //   borderColor: (theme) => theme.palette.primary.main,
  // };
  // const unSelectedStyle: CardProps['sx'] = {
  //   bgcolor: (theme) => 'white',
  //   color: 'black',
  //   borderColor: (theme) => theme.palette.borderColor,
  // };
  const disabledStyle: CardProps['sx'] = {
    opacity: 0.5,
    cursor: 'not-allowed',
  };

  return (
    <Box
      display='flex'
      justifyContent='center'
      alignItems='center'
      onClick={
        !isDisabled ? () => handleItemClick(`${option.value}`) : undefined
      }
      border='1px solid transparent'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        position: 'relative',
        padding: '18px 12px',
        borderRadius: '10px',
        boxShadow: (theme) => theme.shadows[1],
        cursor: !isDisabled ? 'pointer' : 'not-allowed',
        transition: 'transform 0.1s',
        ...(isSelected ? selectedStyle : unSelectedStyle),
        ...(isDisabled && disabledStyle),
        '&:hover': {
          boxShadow: (theme) => theme.shadows[2],
        },
        '&:active': { transform: 'scale(0.98)' },
      }}
    >
      {option.iconUrl && (
        <Box
          sx={{
            borderRadius: '50px',
            overflow: 'hidden',
            width: '50px',
            height: '50px',
            ...(!option.iconUrl && {
              outline: (theme) => `1px solid ${theme.palette.primary.dark}`,
            }),
          }}
        >
          <SparkImageLoader
            src={option.iconUrl}
            alt={option.label}
            style={{ width: 'inherit', height: 'inherit' }}
          />
        </Box>
      )}
      <Typography textAlign='center' fontSize='16px' fontFamily='Mont-SemiBold'>
        {option.label}
      </Typography>
      {/* {!!option.description && (
        <Typography
          textAlign='center'
          fontSize='16px'
          variant='subtitle2'
          sx={{
            fontStyle: 'italic',
            ...(!isSelected && { color: 'var(--color-label)' }),
          }}
        >
          {option.description}
        </Typography>
      )} */}
      {/* {isSelected && (
        <Box position='absolute' top='3px' right='3px'>
          <CheckCircleIcon color='success' />
        </Box>
      )} */}
    </Box>
  );
}
